// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/FeedMemoryCard.test.tsx
import { CaretDown, CaretRight, Database } from "@phosphor-icons/react";
import { Button, Text, TextField } from "@radix-ui/themes";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  type ProjectFeedEvent,
  type ProjectMemoryPayload,
} from "../../../services/askPunttProjectFeed.types";
import {
  agentComponentIdAttr,
  CHAT_ATOM_AGENT_COMPONENT_IDS,
} from "../agentComponents/chatAtomRegistry";

export type FeedMemoryCardVariant =
  | "open"
  | "edit"
  | "changeScope"
  | "editedOpen";

export type FeedMemoryScopeOption = {
  id: string;
  label: string;
  description?: string;
};

/**
 * Feed confirmation for a remembered instruction. Collapsible AgentSteps-style
 * header (Figma FeedMemoryCard 49613:961) — no panel fill/stroke. Variants add
 * Edit / Change Scope / Save via callbacks only; there is no persist API here.
 */
export function FeedMemoryCard({
  event,
  variant = "open",
  open: openProp,
  onOpenChange,
  onEdit,
  onSave,
  onChangeScope,
  onCancel,
  source,
  scope,
}: {
  event: ProjectFeedEvent;
  variant?: FeedMemoryCardVariant;
  /** When set, controls expand/collapse like EnterpriseImpact. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onEdit?: () => void;
  onSave?: (instruction: string) => void;
  onChangeScope?: () => void;
  onCancel?: () => void;
  source?: string;
  scope?: string;
  /** @deprecated Figma changeScope is a text field; kept so old callers type-check. */
  scopes?: FeedMemoryScopeOption[];
  selectedScopeIds?: string[];
  onToggleScope?: (id: string) => void;
}) {
  const { t } = useTranslation("ui");
  const payload = (event.payload ?? {}) as ProjectMemoryPayload;
  const instruction = payload.instruction ?? event.body ?? "";
  const [draft, setDraft] = useState(instruction);
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isControlled = openProp != null;
  const expanded = isControlled ? openProp : uncontrolledOpen;
  const heading = t("feedMemoryCard.savedToProjectMemory");
  const showActions = Boolean(onEdit || onChangeScope);
  const editing = variant === "edit";
  const scoping = variant === "changeScope";
  const edited = variant === "editedOpen";
  // why: edit/changeScope must stay visible while drafting — don't collapse mid-edit.
  const bodyVisible = expanded || editing || scoping;

  const setExpanded = (next: boolean) => {
    if (!isControlled) setUncontrolledOpen(next);
    onOpenChange?.(next);
  };

  return (
    <section
      {...agentComponentIdAttr(CHAT_ATOM_AGENT_COMPONENT_IDS.feedMemoryCard)}
      // why: match EnterpriseImpact — header + body only, no muted panel chrome.
      className="flex flex-col gap-2 bg-transparent"
      data-testid="project-feed-memory-saved"
      data-variant={variant}
      aria-label={heading}
    >
      <button
        type="button"
        data-testid="feed-memory-toggle"
        aria-expanded={bodyVisible}
        aria-label={
          bodyVisible
            ? t("feedMemoryCard.collapseAriaLabel")
            : t("feedMemoryCard.expandAriaLabel")
        }
        onClick={() => {
          if (editing || scoping) return;
          setExpanded(!expanded);
        }}
        // why: header label must not sit on muted gray-2 (AgentSteps toggle chrome).
        className="flex w-full items-center justify-between bg-transparent text-left outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-puntt-accent-7"
      >
        <span className="flex items-center gap-2.5 bg-transparent">
          <Database
            size={16}
            className="shrink-0 text-puntt-neutral-gray-11"
            aria-hidden="true"
          />
          <Text size="1" className="bg-transparent text-puntt-neutral-gray-11">
            {heading}
          </Text>
        </span>
        {bodyVisible ? (
          <CaretDown
            size={16}
            aria-hidden="true"
            className="shrink-0 text-puntt-neutral-gray-11"
          />
        ) : (
          <CaretRight
            size={16}
            aria-hidden="true"
            className="shrink-0 text-puntt-neutral-gray-11"
          />
        )}
      </button>

      {bodyVisible ? (
        editing ? (
          <div className="flex flex-col gap-1">
            <TextField.Root
              id={`feed-memory-instruction-${event._id}`}
              data-testid="feed-memory-instruction-input"
              size="2"
              aria-label={t("feedMemoryCard.instructionLabel")}
              placeholder={t("feedMemoryCard.instructionLabel")}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
            />
            <div className="flex items-center gap-2">
              <Button
                type="button"
                data-testid="feed-memory-save"
                size="1"
                variant="outline"
                className="inline-flex h-6 items-center"
                disabled={draft.trim().length === 0}
                onClick={() => onSave?.(draft.trim())}
              >
                {t("feedMemoryCard.save")}
              </Button>
              {onCancel ? (
                <Button
                  type="button"
                  data-testid="feed-memory-cancel"
                  size="1"
                  variant="ghost"
                  className="inline-flex h-6 items-center"
                  onClick={onCancel}
                >
                  {t("feedMemoryCard.cancel")}
                </Button>
              ) : null}
            </div>
          </div>
        ) : scoping ? (
          <div className="flex flex-col gap-1">
            <TextField.Root
              data-testid="feed-memory-scope-input"
              size="2"
              aria-label={t("feedMemoryCard.scopeHeading")}
              placeholder={t("feedMemoryCard.scopeHeading")}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
            />
            <div className="flex items-center gap-2">
              <Button
                type="button"
                data-testid="feed-memory-save"
                size="1"
                variant="outline"
                className="inline-flex h-6 items-center"
                onClick={() => onSave?.(draft.trim() || instruction)}
              >
                {t("feedMemoryCard.save")}
              </Button>
              {onCancel ? (
                <Button
                  type="button"
                  data-testid="feed-memory-cancel"
                  size="1"
                  variant="ghost"
                  className="inline-flex h-6 items-center"
                  onClick={onCancel}
                >
                  {t("feedMemoryCard.cancel")}
                </Button>
              ) : null}
            </div>
          </div>
        ) : (
          <>
            {instruction ? (
              <div className="flex flex-col gap-1 bg-transparent">
                <Text
                  as="p"
                  size="1"
                  weight={edited ? "medium" : "regular"}
                  className="bg-transparent text-puntt-neutral-gray-12"
                  data-testid="project-feed-memory-instruction"
                >
                  {instruction}
                </Text>
                {source ? (
                  <Text
                    as="p"
                    size="1"
                    className="bg-transparent text-puntt-neutral-gray-11"
                    data-testid="project-feed-memory-source"
                  >
                    {source}
                  </Text>
                ) : null}
                {scope ? (
                  <Text
                    as="p"
                    size="1"
                    className="bg-transparent text-puntt-neutral-gray-11"
                    data-testid="project-feed-memory-scope"
                  >
                    {scope}
                  </Text>
                ) : null}
              </div>
            ) : null}
            {showActions ? (
              <div className="flex items-center gap-1">
                {onEdit ? (
                  <Button
                    type="button"
                    data-testid="feed-memory-edit"
                    size="1"
                    variant="outline"
                    className="inline-flex h-6 items-center"
                    onClick={onEdit}
                  >
                    {t("feedMemoryCard.edit")}
                  </Button>
                ) : null}
                {onChangeScope ? (
                  <Button
                    type="button"
                    data-testid="feed-memory-change-scope"
                    size="1"
                    variant="ghost"
                    className="inline-flex h-6 items-center"
                    onClick={onChangeScope}
                  >
                    {t("feedMemoryCard.changeScope")}
                  </Button>
                ) : null}
              </div>
            ) : null}
          </>
        )
      ) : null}
    </section>
  );
}
